import useAsync from 'react-use/lib/useAsync';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  InfoCard,
  Progress,
  ResponseErrorPanel,
  Link,
} from '@backstage/core-components';
import {
  Box,
  Chip,
  Divider,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {
  CRITICAL_SERVICE_ID,
  GOALERT_QUERY_DOCUMENT,
  NON_CRITICAL_SERVICE_ID,
  ServiceOnCall,
} from './query';

// Literal values rather than theme references — `theme.typography.fontWeightBold`
// types as `string | number | undefined`, which trips the makeStyles type
// checker (CSS fontWeight doesn't accept undefined). Theme integration isn't
// worth the wrestling for six small style rules.
const useStyles = makeStyles({
  severityHeading: {
    fontSize: '0.875rem',
    fontWeight: 700,
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    marginBottom: 8,
  },
  stepRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'baseline',
    fontSize: '0.75rem',
    opacity: 0.7,
  },
  stepDelay: {
    minWidth: 40,
    fontVariantNumeric: 'tabular-nums',
  },
  severityBlock: {
    marginBottom: 16,
  },
  loopSuffix: {
    marginLeft: 8,
    fontStyle: 'italic',
  },
  footer: {
    marginTop: 12,
    display: 'block',
  },
  empty: {
    fontStyle: 'italic',
    opacity: 0.5,
    fontSize: '0.8125rem',
  },
});

const GOALERT_BASE_URL = 'https://goalert.heystaq.com';

// Map GoAlert target-type to its deep-link URL. Returns null for target
// types without a navigable URL in GoAlert's UI (e.g., webhooks).
function goalertTargetUrl(type: string, id: string): string | null {
  switch (type) {
    case 'service':
      return `${GOALERT_BASE_URL}/services/${id}`;
    case 'schedule':
      return `${GOALERT_BASE_URL}/schedules/${id}`;
    case 'user':
      return `${GOALERT_BASE_URL}/users/${id}`;
    case 'rotation':
      return `${GOALERT_BASE_URL}/rotations/${id}`;
    case 'slackChannel':
      return `${GOALERT_BASE_URL}/slack-channels/${id}`;
    case 'slackUserGroup':
      return `${GOALERT_BASE_URL}/slack-user-groups/${id}`;
    default:
      return null;
  }
}

type ServiceQueryResult = {
  data?: { service: ServiceOnCall | null };
  errors?: Array<{ message: string }>;
};

function useGoAlertOnCall(serviceId: string) {
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  return useAsync(async (): Promise<ServiceOnCall | null> => {
    if (!serviceId) return null;
    // proxy plugin serves under <backend>/api/proxy/<endpoint>; the
    // app-config `proxy.endpoints./goalert.target` is the GoAlert host, so
    // /api/proxy/goalert/api/graphql → https://goalert.heystaq.com/api/graphql.
    const proxyBase = await discovery.getBaseUrl('proxy');
    const url = `${proxyBase}/goalert/api/graphql`;
    const response = await fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: GOALERT_QUERY_DOCUMENT,
        operationName: 'BackstageServiceOnCall',
        variables: { serviceID: serviceId },
      }),
    });
    if (!response.ok) {
      throw new Error(
        `GoAlert proxy returned ${response.status} ${response.statusText}`,
      );
    }
    const body = (await response.json()) as ServiceQueryResult;
    if (body.errors?.length) {
      throw new Error(
        `GoAlert GraphQL error: ${body.errors.map(e => e.message).join('; ')}`,
      );
    }
    return body.data?.service ?? null;
  }, [serviceId]);
}

function stepOneUsers(service: ServiceOnCall | null) {
  if (!service) return [] as Array<{ userID: string; userName: string }>;
  return service.onCallUsers.filter(
    u => u.stepNumber === 0 || u.stepNumber === 1,
  );
}

function SeverityBlock(props: {
  severity: 'CRITICAL' | 'non-CRITICAL';
  serviceId: string;
  service: ServiceOnCall | null;
  loading: boolean;
}) {
  const classes = useStyles();
  const onCall = stepOneUsers(props.service);
  const serviceUrl = `${GOALERT_BASE_URL}/services/${props.serviceId}`;

  return (
    <Box className={classes.severityBlock}>
      <Typography component="h3" className={classes.severityHeading}>
        <Link to={serviceUrl}>{props.severity}</Link>
      </Typography>
      {props.loading ? null : onCall.length === 0 ? (
        <Typography className={classes.empty}>
          No one currently on the first step.
        </Typography>
      ) : (
        <Box className={classes.chips}>
          {onCall.map(u => (
            <Link
              key={u.userID}
              to={`${GOALERT_BASE_URL}/users/${u.userID}`}
            >
              <Chip size="small" label={u.userName} clickable />
            </Link>
          ))}
        </Box>
      )}
      {(props.service?.escalationPolicy.steps ?? []).map((step, idx, steps) => {
        // GoAlert's delayMinutes on a step is the wait BEFORE moving to the
        // NEXT step. Each step's display time is the cumulative sum of the
        // previous steps' delays, expressed as plain minutes.
        const fireAt = steps
          .slice(0, idx)
          .reduce((sum, s) => sum + s.delayMinutes, 0);
        const isLast = idx === steps.length - 1;
        return (
          <Box key={idx} className={classes.stepRow}>
            <span className={classes.stepDelay}>
              {fireAt === 0 ? '0m' : `${fireAt}m`}
            </span>
            <span>
              {step.targets.length === 0
                ? '(no targets)'
                : step.targets.map((t, ti) => {
                    const url = goalertTargetUrl(t.type, t.id);
                    return (
                      <span key={t.id}>
                        {ti > 0 ? ', ' : ''}
                        {url ? <Link to={url}>{t.name}</Link> : t.name}
                      </span>
                    );
                  })}
              {isLast && step.delayMinutes > 0 ? (
                <span className={classes.loopSuffix}>
                  loops after {step.delayMinutes}m
                </span>
              ) : null}
            </span>
          </Box>
        );
      })}
    </Box>
  );
}

export function OnCallCard() {
  const classes = useStyles();

  const critical = useGoAlertOnCall(CRITICAL_SERVICE_ID);
  const nonCritical = useGoAlertOnCall(NON_CRITICAL_SERVICE_ID);

  const error = critical.error ?? nonCritical.error;
  const loading = critical.loading || nonCritical.loading;

  return (
    <InfoCard title="On-call (Aknostic)">
      {loading && <Progress />}
      {error && <ResponseErrorPanel error={error as Error} />}
      {!loading && !error && (
        <Box>
          <SeverityBlock
            severity="CRITICAL"
            serviceId={CRITICAL_SERVICE_ID}
            service={critical.value ?? null}
            loading={critical.loading}
          />
          <SeverityBlock
            severity="non-CRITICAL"
            serviceId={NON_CRITICAL_SERVICE_ID}
            service={nonCritical.value ?? null}
            loading={nonCritical.loading}
          />
        </Box>
      )}
      <Divider />
      <Typography
        variant="caption"
        color="textSecondary"
        className={classes.footer}
      >
        Aknostic platform team carries the pager for every entity in the
        catalog. Same rotation across all catalog entries.
      </Typography>
    </InfoCard>
  );
}
